'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import type { FC } from 'react';
import { useIntl } from 'react-intl';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';
import { TextWithDialog } from '../../../TextWithDialog/TextWithDialog.client';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking';
import { PromotionComponentLayout3Messages } from './messages';

export type Layout3Props = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component Layout 3
 *
 * component to promote the installment calculator in layout version 3
 *
 * */
export const Layout3: FC<Layout3Props> = ({
  singleInstallment,
  currency,
  openInstallmentCalulatorHandler,
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  return (
    <Card className="bg-color-grey-light space-y-1.5 p-1.5">
      <div className="flex flex-nowrap gap-1.5">
        <div className="bg-color-additional flex h-6 w-6 items-center justify-center rounded">
          <span className="icon-emp-installments bg-color-background-base h-4 w-4" />
        </div>
        <div className="space-y-1">
          <p className="text-size-body1">
            {formatMessage(PromotionComponentLayout3Messages.rateText, {
              value: formatPrice(singleInstallment, currency),
            })}
          </p>
          <Button
            onClick={() => {
              openInstallmentCalulatorHandler();
              trackGlycerinLinkClick();
            }}
          >
            {formatMessage(PromotionComponentLayout3Messages.buttonText)}
          </Button>
        </div>
      </div>
      <Divider flexItem />
      <p className="text-size-body1">
        {formatMessage(PromotionComponentLayout3Messages.legalText, {
          button: (msg) => (
            <TextWithDialog
              message={msg.join(' ')}
              dialogTitle={formatMessage(PromotionComponentLayout3Messages.modalHeadline)}
              dialogContent={<ServiceLayer serviceLayerId="installment_ratenelement" />}
              displayTrackingProps={{ category: 'dv-service-layer-layout-3' }}
              aria-label={formatMessage(PromotionComponentLayout3Messages.legalText, {
                button: (message) => message.join(' '),
              })}
            />
          ),
        })}
      </p>
    </Card>
  );
};
